import { CommonModule } from '@angular/common'
import { Component, Input } from '@angular/core'
import { Router, RouterModule } from '@angular/router'
import { ContentStackService } from '@app/core/_services/content-stack.service'
import { UserService } from '@app/core/_services/user.service'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { faMessage, faVideo } from '@fortawesome/pro-regular-svg-icons'
import { faCalendar, faStar } from '@fortawesome/pro-solid-svg-icons'
import { FmAvatarComponent } from '@app/shared/components/fm-avatar/fm-avatar.component'
import { FmIconComponent } from '@app/shared/components/fm-icon/fm-icon.component'
import { ButtonModule } from 'primeng/button'
import { HomeWelcomeMessageComponent } from '../home-welcome-message/home-welcome-message.component'
import { Subject, takeUntil } from 'rxjs'
import { User } from '@app/core/_models'
import { AmbassadorSessionsBadgeComponent } from '@app/shared/components/ambassador-sessions-badge/ambassador-sessions-badge.component'
import { HostBadgeComponent } from '@app/shared/components/host-badge/host-badge.component'
import { RewardsReferralService } from '@app/core/_services/rewards-referral.service'
import { AnalyticsService } from '@app/core/_services/analytics.service'
import { TooltipModule } from 'primeng/tooltip'
import { MessageModule } from 'primeng/message'
import { MeetingsService, ConfirmedSessionsService, FeatureCheckService } from '@app/core/_services'
import { TagModule } from 'primeng/tag'

type RpHomeAnalyticsSource = 'dynamicPanelHomePanelOpen' | 'dynamicPanelHomePanelClosed'

@Component({
    selector: 'fm-rp-home',
    imports: [
        TooltipModule,
        CommonModule,
        ButtonModule,
        FontAwesomeModule,
        RouterModule,
        FmAvatarComponent,
        FmIconComponent,
        HomeWelcomeMessageComponent,
        AmbassadorSessionsBadgeComponent,
        HostBadgeComponent,
        MessageModule,
        TagModule,
    ],
    templateUrl: './rp-home.component.html',
    styleUrl: './rp-home.component.scss',
})
export class RpHomeComponent {
    @Input() panelOpen: boolean = true

    faCalendar = faCalendar
    faStar = faStar
    faVideo = faVideo
    faMessage = faMessage

    private ngUnsubscribe: Subject<any> = new Subject<any>()
    public currentUser: User = new User()
    public planNameString: string = ''
    public showUpgradeButton: boolean
    public trialDaysRemaining: number | null = null

    constructor(
        private contentStackService: ContentStackService,
        private userService: UserService,
        private rewardsReferralService: RewardsReferralService,
        private analyticsService: AnalyticsService,
        public confirmedSessionsService: ConfirmedSessionsService,
        private router: Router,
        public featureCheckService: FeatureCheckService,
        public meetingsService: MeetingsService,
    ) {}

    ngOnInit() {
        this.userService.currentUser.pipe(takeUntil(this.ngUnsubscribe)).subscribe((user) => {
            this.currentUser = { ...user }
            this.setPlanNameSInfo()
        })
    }

    private setPlanNameSInfo(): void {
        this.trialDaysRemaining = this.rewardsReferralService.getTrialDaysRemaining(
            this.currentUser,
        )

        if (this.currentUser.planInfo.productAccess == 'Focusmate Free') {
            this.showUpgradeButton = true
            if (this.trialDaysRemaining && this.trialDaysRemaining > 0) {
                this.planNameString = 'Focusmate Plus Trial'
            } else {
                this.planNameString = 'Free Plan · 3 sessions per week'
            }
        } else {
            this.showUpgradeButton = false
            this.planNameString = this.currentUser.planInfo.productAccess
        }
    }

    private getSource(): RpHomeAnalyticsSource {
        if (this.panelOpen) {
            return 'dynamicPanelHomePanelOpen'
        }
        return 'dynamicPanelHomePanelClosed'
    }

    onClickAvatar() {
        this.analyticsService.logClickedEvent('My Profile - Image', this.getSource())
        this.router.navigate(['/profile'])
    }
    onClickProfileButton(): void {
        this.analyticsService.logClickedEvent('My Profile - Button', this.getSource())
    }
    onClickYourSchedule() {
        this.analyticsService.logClickedEvent('My Schedule', this.getSource())
        this.contentStackService.pushMyScheduleToDynamicPanel(true)
    }

    onClickGroupSessions(): void {
        if (!this.featureCheckService.groupMeetingsEnabledSig()) {
            return
        }
        this.analyticsService.logGroupMeetingClick('openGroupMeetings', this.getSource())
        this.contentStackService.pushGroupMeetingsToDynamicPanel(true)
    }

    onClickUpgrade(): void {
        this.analyticsService.logClickedEvent('Upgrade to Plus', this.getSource())
    }

    onClickFavoritesSchedule(): void {
        this.analyticsService.logClickedEvent('Favorites Schedule', this.getSource())
        this.contentStackService.pushFavoritesAvailabilityListToDynamicPanel(true)
    }

    onClickTestAutioAndVideo(): void {
        this.analyticsService.logClickedEvent('Test audio and video', this.getSource())
    }

    onClickTaskManager(): void {
        this.analyticsService.logClickedEvent('Task Manager', this.getSource())
        this.contentStackService.pushTaskManagerToDynamicPanel(true)
    }

    isTaskManagerEnabled(): boolean {
        return this.featureCheckService.isTaskManagerEnabled(this.currentUser)
    }
}
